import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import CurrencyInput from '../../components/CurrencyInput';
import api from '../../services/api';
import { showSuccess, showError } from '../../services/alert';

const OperacaoCampoForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    safraId: '',
    talhaoId: '',
    maquinaId: '',
    tipo: '',
    data: '',
    descricao: '',
    horasTrabalhadas: '',
    custoOperacional: '',
  });
  const [loading, setLoading] = useState(false);
  const [safras, setSafras] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);

  useEffect(() => {
    const loadSafras = async () => {
      try {
        const [resSafras, resTalhoes, resMaquinas] = await Promise.all([
          api.get('/safras'),
          api.get('/talhoes'),
          api.get('/maquinas'),
        ]);
        setSafras(resSafras.data);
        setTalhoes(resTalhoes.data);
        setMaquinas(resMaquinas.data);
      } catch {
        showError('Erro ao carregar safras.');
      }
    };
    loadSafras();

    if (isEditing) {
      setLoading(true);
      api.get(`/operacoescampo/${id}`)
        .then(res => setForm(res.data))
        .catch(() => showError('Erro ao carregar operação.'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (!form.maquinaId || !form.horasTrabalhadas) return;

    const maquina = maquinas.find(item => String(item.id) === String(form.maquinaId));
    if (!maquina) return;

    const custo = Number(form.horasTrabalhadas || 0) * Number(maquina.custoHora || 0);
    setForm(prev => ({ ...prev, custoOperacional: custo.toFixed(2) }));
  }, [form.maquinaId, form.horasTrabalhadas, maquinas]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...form,
      safraId: Number(form.safraId),
      talhaoId: form.talhaoId ? Number(form.talhaoId) : null,
      maquinaId: form.maquinaId ? Number(form.maquinaId) : null,
      horasTrabalhadas: Number(form.horasTrabalhadas || 0),
      custoOperacional: Number(form.custoOperacional || 0),
    };

    try {
      if (isEditing) {
        await api.put(`/operacoescampo/${id}`, payload);
        showSuccess('Operação atualizada com sucesso.');
      } else {
        await api.post('/operacoescampo', payload);
        showSuccess('Operação criada com sucesso.');
      }
      navigate('/operacoes-campo');
    } catch {
      showError('Erro ao salvar operação.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/operacoes-campo')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>
          <h5 className="mb-0">{isEditing ? 'Editar operação' : 'Nova operação'}</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold">Safra *</label>
              <select
                name="safraId"
                value={form.safraId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {safras.map(s => (
                  <option key={s.id} value={s.id}>{s.cultura}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Tipo *</label>
              <select
                name="tipo"
                value={form.tipo}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>
                <option value="Plantio">Plantio</option>
                <option value="Pulverização">Pulverização</option>
                <option value="Colheita">Colheita</option>
                <option value="Outro">Outro</option>
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Talhão</label>
              <select
                name="talhaoId"
                value={form.talhaoId || ''}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {talhoes.map(t => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Máquina</label>
              <select
                name="maquinaId"
                value={form.maquinaId || ''}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {maquinas.map(m => (
                  <option key={m.id} value={m.id}>{m.descricao}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Data *</label>
              <input
                type="date"
                name="data"
                value={form.data?.substring(0, 10) || ''}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Horas trabalhadas</label>
              <input
                type="number"
                name="horasTrabalhadas"
                value={form.horasTrabalhadas || ''}
                onChange={handleChange}
                className="form-control"
                min="0"
                step="0.01"
                disabled={loading}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label fw-semibold">Custo operacional</label>
              <CurrencyInput
                name="custoOperacional"
                value={form.custoOperacional || ''}
                onChange={handleChange}
                className="form-control"
                placeholder="0,00"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label fw-semibold">Descrição</label>
              <textarea
                name="descricao"
                value={form.descricao}
                onChange={handleChange}
                className="form-control"
                rows={3}
                disabled={loading}
              />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                <i className="bi bi-save"></i>
                <span>{loading ? 'Salvando...' : 'Salvar'}</span>
              </button>
              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-1"
                onClick={() => navigate('/operacoes-campo')}
                disabled={loading}
              >
                <i className="bi bi-x-circle"></i>
                <span>Cancelar</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OperacaoCampoForm;
